// R25 — THE DOOR TO DISCUSS WHAT SHE JUST PAID FOR.
//
// N4-3's handoff mechanism: tapping "Ask Lala" on any delivered reading takes the member straight to
// the Ask Lala room with the reading already handed to her, nothing retyped. ONE SHARED COMPONENT per
// the consolidation law — every surface renders this button, the button dispatches the event, App is
// the only listener. A reading surface added tomorrow gets the behaviour by rendering it.
//
// *** THE READING TRAVELS IN MEMORY, NEVER IN THE URL. *** That is the standing privacy decision, and
// it is why this is an event carrying an object rather than a query string or a route param: a
// member's dream, chart or palm reading must never sit in a browser history entry, a server log or a
// shared link.
//
// WHY IT CAME OUT OF App.tsx (R25, roadmap 7). The button carries a DECISION, and the decision is a
// CROSSING with silence as its default:
//
//     server/index.js        ROOM_FOR_TYPE + every saved-report type — 33 types the server can write
//     src/askLalaHandoff.js  ASK_LALA_READING_TYPES — the 21 that get this door
//     the line below         `if (!ASK_LALA_READING_TYPES.has(t)) return null;`
//
// A reading type the server writes and nobody added to that Set delivers WITH NO ASK LALA BUTTON.
// Nothing throws, nothing logs, and the member cannot tell an omission from a room that simply does
// not offer it. She paid for the reading; the door to discuss it is missing. Out here the button is
// hookless, so `scripts/r25-reading-door.mjs` CALLS it once per type the SERVER can write and reads
// the tree back, instead of a regex confirming the line is present (which it always is).
//
// The law it holds is an exact biconditional and it is correct today: every type filing to a reading
// room offers this door; every type filing to `magic` refuses it, because W12's law is that a working
// is not a reading. `seal` refuses it by the owner's own ruling — a seal is a made object, not a
// reading to discuss.
import { MessageCircle } from "lucide-react";
// R184 — the roster's own PREDICATE rather than a fourth copy of `.has(...)`. That expression was
// written out here, in `handoffKindFor` and in `memberSpokenText` while `isAskLalaReading` — the
// function whose entire body it is — was called by nothing (R28/R29 + R38's trap). The comment above
// still names the crossing correctly; what has changed is that this file no longer re-answers it.
import { isAskLalaReading } from "../askLalaHandoff.js";

// App is the only listener. Exported so the listener and the dispatcher can never name it differently.
export const ASK_LALA_READING_EVENT = "twelve:ask-lala-reading";

export function askLalaAboutReading(detail: { report: unknown; kind: string; subject?: string | null }) {
  window.dispatchEvent(new CustomEvent(ASK_LALA_READING_EVENT, { detail }));
}

// Rendered in every delivered reading's action row, alongside Download PDF.
// NOT on the Seal Creator, per the owner's ruling: a seal is a made object, not a reading to discuss.
// N16-4: the aqua glow is ONE token on this shared component, so every surface it appears on carries
// it and no surface can drift. Callers may still pass their own layout class; the token is appended.
export function AskLalaAboutButton({ report, kind, subject, className = "button button--ghost" }: {
  report: unknown; kind: string; subject?: string | null; className?: string;
}) {
  if (!report) return null;
  if (!isAskLalaReading(report)) return null;
  return (
    <button
      type="button"
      className={`${className} ask-lala-btn`}
      onClick={() => askLalaAboutReading({ report, kind, subject })}
      aria-label="Ask Lala about this reading"
    >
      <MessageCircle size={15} /> Ask Lala
    </button>
  );
}
